import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AWARD_PAYMENT } from '../../constants/general';
import TitleBar from '../Shared/TitleBar';
import '../../tailwind.css';
import AwardPaymentRequests from '../AwardPayment/AwardPaymentRequests';
import { type AwardPaymentRequest } from '../../types/awardPayment';
import PleaseWait from '../Shared/PleaseWait';
import { inject, observer } from 'mobx-react';
import { formatCurrencyAmount, isAdminOrFHT, isScholarshipCoordinator, isStudentOrAlumni } from '../../util/miscUtil';
import { callApi } from '../../util/apiWrapper';
import API from '../../constants/api';
import { METHOD } from '../../constants/method';
import { dateToS } from '../../util/dateUtil';
import AwardPaymentLayout from '../AwardPayment/AwardPaymentLayout';
import AwardPaymentPageContainer from '../AwardPayment/AwardPaymentPageContainer';
import { logException } from '../../services/appInsights';
import { AuthStoreShape } from '../../types/authStore';
import AwardPaymentRequestsHeader from '../AwardPayment/AwardPaymentRequestsHeader';
import { parseDateTime } from '../AwardPayment/awardUtils';
import { buildAwardPaymentTitleBarPaths } from '../../util/awardPaymentTitleBar';

type PaymentRequestApiItem = {
  submitted_at?: string;
  created_at?: string;
  updated_at?: string;
  status?: string;
  amount_requested?: string;
  award_type?: string;
  id?: number;
  first_name?: string;
  last_name?: string;
  created_by?: number;
  user_id?: number;
  schoolInfo?: {
    date_of_enrollment?: string | null;
    planned_graduation_date?: string | null;
  } | null;
  requestNumber?: string;
};

type PaymentRequestsUserResponse = {
  result?: PaymentRequestApiItem[];
};

export const fetchAwardPaymentRequests = async (auth: AuthStoreShape, userId?: number): Promise<AwardPaymentRequest[]> => {
  const response = userId ? (await callApi(auth, API.PAYMENT_REQUESTS_USER.replace('{userId}', userId.toString()), METHOD.GET)) as PaymentRequestsUserResponse : await callApi(auth, API.PAYMENT_REQUEST, METHOD.GET) as PaymentRequestsUserResponse;
  const result = Array.isArray(response?.result) ? response.result : [];
  return result.map((each) => {
    const submittedAtTimestamp = each?.submitted_at ? parseDateTime(each.submitted_at)?.toMillis() ?? 0 : 0;
    return {
      id: each.id ?? 0,
      type: each.award_type ?? '',
      submittedAt: each?.submitted_at ? dateToS(each?.submitted_at) : '',
      submittedAtTimestamp,
      updatedAt: each?.updated_at ?? '',
      amountRequested: formatCurrencyAmount(each.amount_requested),
      amountRequestedValue: Number(each.amount_requested ?? 0) || 0,
      status: each.status ?? '',
      firstName: each.first_name ?? '',
      lastName: each.last_name ?? '',
      createdAt: each?.created_at ? dateToS(each?.created_at) : '',
      userId: each?.user_id ?? 0,
      schoolInfo: each?.schoolInfo
        ? {
            dateOfEnrollment: each.schoolInfo.date_of_enrollment ?? '',
            plannedGraduationDate: each.schoolInfo.planned_graduation_date ?? '',
          }
        : null,
      requestNumber: each.requestNumber ?? ''
    };
  });
};

type Props = { AuthStore?: AuthStoreShape; SeniorStore?: any };

const AwardPaymentRequestsSummaryComponent: React.FC<Props> = (props) => {
  const [allRequests, setAllRequests] = useState<AwardPaymentRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const {userId, isAwardPaymentReviewer, isStudentOrAlumniRole, isAdminOrFHTRole, showTitleBar,isUserAlumni} = useMemo(() => {
    const auth = props.AuthStore ?? { roles: [], user: { result: { id: 0 , position_title: '' } } };
    const store = props.SeniorStore ?? { senior: { userId: 0 } };

    const isStudentOrAlumniRole = isStudentOrAlumni(auth?.roles);
    const isAdminOrFHTRole = isAdminOrFHT(auth?.roles);
    const isScholarshipCoordinatorUser = isScholarshipCoordinator(auth.roles, auth?.user?.result?.position_title);
    if (isAdminOrFHTRole) {
      return {
        userId: store?.senior?.userId ?? 0,
        isAwardPaymentReviewer: isScholarshipCoordinatorUser,
        isStudentOrAlumniRole,
        isAdminOrFHTRole,
        showTitleBar: true,
        isUserAlumni: store?.alumni
      };
    }
    if (isStudentOrAlumniRole) {
      return {
        userId: auth?.user?.result?.id ?? 0,
        isAwardPaymentReviewer: isScholarshipCoordinatorUser,
        isStudentOrAlumniRole,
        isAdminOrFHTRole,
        showTitleBar: false,
        isUserAlumni: store?.alumni
      };
    }
    return {
      userId: auth?.user?.result?.id ?? 0,
      isAwardPaymentReviewer: isScholarshipCoordinatorUser,
      isStudentOrAlumniRole,
      isAdminOrFHTRole,
      showTitleBar: true,
      isUserAlumni: store?.alumni
    };
  }, [props.AuthStore, props.SeniorStore]);

  const history = useHistory();
  const senior = props.SeniorStore?.senior ?? {};
  const seniorDisplayName = [
    senior?.alumni_updated_first_name ?? senior?.first_name,
    senior?.alumni_updated_last_name ?? senior?.last_name,
  ].filter(Boolean).join(' ').trim();

  const titleBarPaths = useMemo(() => buildAwardPaymentTitleBarPaths({
    history,
    roles: props.AuthStore?.roles ?? [],
    SeniorStore: props.SeniorStore,
    isAdminOrFHTRole,
    isUserAlumni,
    seniorDisplayName,
    requestContextUserId: userId,
  }), [history, props.AuthStore, props.SeniorStore, isAdminOrFHTRole, isUserAlumni, seniorDisplayName, userId]);

  useEffect(() => {
    if (isAwardPaymentReviewer && typeof props.SeniorStore?.resetSenior === 'function') {
      props.SeniorStore.resetSenior();
    }
  }, [isAwardPaymentReviewer, props.SeniorStore]);

  useEffect(() => {
    let isMounted = true;

    const loadAwardPaymentRequests = async () => {
      // If coordinator/reviewer and we have cached data from the dashboard, use it and avoid fetching again
      if (isMounted && isAwardPaymentReviewer) {
        try {
          const cached = typeof window !== 'undefined' ? sessionStorage.getItem(AWARD_PAYMENT.AWARD_PAYMENT_REQUESTS_CACHE) : null;
          if (cached) {
            const parsed: AwardPaymentRequest[] = JSON.parse(cached);
            setAllRequests(parsed);
            setIsLoading(false);
            return;
          }
        } catch (e) {
          // fall through to normal fetch on parse errors
          console.warn('Failed to read cached award payment requests', e);
        }
      }

      if((!userId || userId === 0) && !isAwardPaymentReviewer) {
        setIsLoading(false);
        return;
      }
      if (isMounted) {
        setIsLoading(true);
      }
      const auth = props.AuthStore ?? { roles: [], user: { result: { id: 0 , position_title: '' } } };
      try {
        const fetchedRequests = await fetchAwardPaymentRequests(auth, userId);
        if (isMounted) {
          setAllRequests(fetchedRequests);
        }
      } catch (error) {
        logException(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadAwardPaymentRequests();

    return () => {
      isMounted = false;
    };
  }, [userId, props.AuthStore]);

  return (
    <AwardPaymentLayout dataTestId='award-payment-requests-summary-page' showBanner={!isAwardPaymentReviewer} >
      {showTitleBar && <TitleBar currentPage={AWARD_PAYMENT.TITLE} paths={titleBarPaths}/>}
      <AwardPaymentPageContainer>
        <AwardPaymentRequestsHeader showAction={isStudentOrAlumniRole || isAdminOrFHTRole}/>
        {isLoading ? (
          <div data-testid='award-payment-requests-spinner'>
            <PleaseWait isLoading={isLoading} />
          </div>
        ) : (
          <AwardPaymentRequests allRequests={allRequests} isReviewerView={isAwardPaymentReviewer} setIsLoading={setIsLoading} />
        )}
      </AwardPaymentPageContainer>
    </AwardPaymentLayout>
  );
};

const AwardPaymentRequestsSummary = inject('AuthStore','SeniorStore')(observer(AwardPaymentRequestsSummaryComponent));

export default AwardPaymentRequestsSummary;
